import { type AuthProviders } from '@shared/api'
import { useQuery } from '@tanstack/react-query'
import { Eye, Hand, MessagesSquare } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Logo } from '@/components/Logo'
import { useI18n } from '@/i18n/i18n'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { api } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import { readPref, writePref } from '@/lib/storage'

// What a signed-out visitor sees: what moot is, in a few plain lines and a
// small picture of a board, next to the way in.

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  )
}

function Point({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 grid size-7 flex-none place-items-center rounded-md bg-surface-2 text-ink-2">
        {icon}
      </span>
      <span>
        <span className="block text-[14px] font-medium">{title}</span>
        <span className="block text-[13.5px] leading-relaxed text-muted">{body}</span>
      </span>
    </li>
  )
}

/**
 * A still picture of a board: a question, a group of considerations the AI
 * added to, and a short thread. Fixed geometry (460×252), so nothing overlaps
 * at any width it is shown at (it is hidden on phones).
 */
function BoardSketch() {
  const { t } = useI18n()
  const line = (w: string) => <div className={`mt-1 h-1.5 rounded-full bg-surface-2 ${w}`} />
  return (
    <div
      aria-hidden="true"
      className="relative h-[252px] w-[460px] max-w-full overflow-hidden rounded-xl border border-line bg-canvas shadow-sm [background-image:radial-gradient(var(--line)_1px,transparent_1px)] [background-size:18px_18px]"
    >
      <svg className="absolute inset-0 size-full" fill="none">
        <path d="M186 52 C 215 52, 215 66, 244 66" stroke="var(--line-strong)" strokeWidth="1.5" />
      </svg>
      <div className="absolute top-5 left-4 w-[170px] rounded-lg border border-line bg-surface p-3 shadow-sm">
        <div className="text-[12.5px] font-semibold">{t('landing.sketch.card1')}</div>
        {line('w-32')}
        {line('w-24')}
      </div>
      <div className="absolute top-4 left-[236px] w-[208px] rounded-lg border border-dashed border-line-strong p-2">
        <div className="mb-1.5 text-[11px] font-medium text-muted">{t('landing.sketch.group')}</div>
        <div className="space-y-1.5">
          <div className="rounded-md border border-line bg-surface px-2 py-1.5 shadow-sm">
            <div className="text-[12px] font-semibold">{t('landing.sketch.card2')}</div>
            {line('w-20')}
          </div>
          <div className="rounded-md border border-line bg-surface px-2 py-1.5 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold">{t('landing.sketch.card3')}</span>
              <Avatar actor={{ kind: 'session', id: 'landing' }} size={12} />
            </div>
            {line('w-24')}
          </div>
        </div>
      </div>
      <div className="absolute top-[168px] left-4 flex max-w-[260px] items-center gap-2 rounded-[4px_14px_14px_14px] border border-(--note-line) bg-(--note) px-2.5 py-1.5 text-[12px] shadow-sm">
        <Avatar actor={{ kind: 'user', id: 'landing' }} size={16} />
        <span className="truncate">{t('landing.sketch.ask')}</span>
      </div>
      <div className="absolute top-[206px] left-10 flex max-w-[404px] items-center gap-2 rounded-[4px_14px_14px_14px] border border-line bg-surface px-2.5 py-1.5 text-[12px] shadow-sm">
        <Avatar actor={{ kind: 'session', id: 'landing' }} size={16} />
        <span className="truncate">{t('landing.sketch.reply')}</span>
      </div>
    </div>
  )
}

/**
 * Where to land after signing in: back to an app's authorization request
 * (MCP, worker/oauth.ts) when that is what sent the person here, else home.
 */
function signInCallback(): string {
  const next = new URLSearchParams(window.location.search).get('next')
  return next?.startsWith('/oauth/authorize?') ? next : '/'
}

function SignInCard({ providers }: { providers: AuthProviders | undefined }) {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'sending' | 'error' | 'limited'>('idle')

  // Development: links are logged rather than emailed, so sign in in one click.
  const devEmail = email.trim() || readPref('dev-email') || 'dev@example.com'
  const devSignIn = async () => {
    setState('sending')
    const r = await authClient.signIn.magicLink({ email: devEmail, callbackURL: signInCallback() })
    const link = r.error ? null : await api.devLink(devEmail).catch(() => null)
    if (!link) {
      setState('error')
      return
    }
    writePref('dev-email', devEmail)
    window.location.assign(link.url)
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-5 shadow-md">
      <h2 className="mb-4 text-[15px] font-semibold">{t('landing.start')}</h2>
      {providers?.google && (
        <Button
          size="lg"
          className="w-full"
          onClick={() => {
            void authClient.signIn.social({ provider: 'google', callbackURL: signInCallback() })
          }}
        >
          <GoogleMark />
          {t('signin.google')}
        </Button>
      )}

      {providers?.google && providers.magicLink && <div className="my-4 h-px bg-line" />}

      {providers?.devSignIn && (
        <>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={state === 'sending'}
            onClick={() => {
              void devSignIn()
            }}
          >
            {t('signin.dev', { email: devEmail })}
          </Button>
          <div className="my-4 h-px bg-line" />
        </>
      )}

      {providers?.magicLink && sentTo !== null && (
        <div role="status" className="space-y-2 text-[13px]">
          <p className="font-medium">{t('signin.checkInbox')}</p>
          <p className="text-muted">
            {providers.devSignIn ? t('signin.sentDev') : t('signin.sentTo', { email: sentTo })}
          </p>
          <button
            type="button"
            onClick={() => {
              setSentTo(null)
            }}
            className="text-[12.5px] text-ai underline-offset-2 hover:underline"
          >
            {t('signin.another')}
          </button>
        </div>
      )}

      {providers?.magicLink && sentTo === null && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setState('sending')
            const to = email.trim()
            void authClient.signIn
              .magicLink({ email: to, callbackURL: signInCallback() })
              .then((r) => {
                if (r.error) setState(r.error.status === 429 ? 'limited' : 'error')
                else {
                  setState('idle')
                  setSentTo(to)
                }
              })
          }}
        >
          <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2" htmlFor="email">
            {t('signin.email')}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
            placeholder={t('signin.emailPlaceholder')}
            className="h-10 w-full rounded-md border border-line bg-bg px-3 text-sm outline-none transition-shadow focus:border-(--focus) focus:shadow-[0_0_0_3px_var(--ai-soft)]"
          />
          <Button
            type="submit"
            size="lg"
            {...(!providers.devSignIn && !providers.google && { variant: 'primary' as const })}
            className="mt-3 w-full"
            disabled={state === 'sending'}
          >
            {t('signin.sendLink')}
          </Button>
        </form>
      )}

      {(state === 'error' || state === 'limited') && (
        <p className="mt-3 text-[12.5px] text-danger" role="alert">
          {t(state === 'limited' ? 'signin.limited' : 'signin.error')}
        </p>
      )}

      {providers && !providers.google && !providers.magicLink && (
        <p className="text-sm text-muted">{t('signin.none')}</p>
      )}
      {!providers && <div className="h-11 animate-pulse rounded-md bg-surface-2" />}
      <p className="mt-4 text-[12px] leading-relaxed text-muted">{t('signin.subtitle')}</p>
    </div>
  )
}

export function SignIn() {
  const { t } = useI18n()
  const config = useQuery({ queryKey: ['config'], queryFn: api.config })

  return (
    <div className="flex min-h-full flex-col">
      <header className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between px-4 sm:px-6">
        <Logo />
        <LanguageSwitch />
      </header>

      <main className="mx-auto grid w-full max-w-5xl flex-1 animate-[rise-in_400ms_var(--ease-spring)] items-start gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_360px] lg:gap-16 lg:py-20">
        <section>
          <h1 className="max-w-[18em] text-[30px] leading-[1.2] font-semibold tracking-[-0.025em] text-balance break-keep sm:text-[36px]">
            {t('landing.headline')}
          </h1>
          <p className="mt-4 max-w-[34em] text-[15px] leading-relaxed text-ink-2">
            {t('landing.lede')}
          </p>
          <ul className="mt-8 max-w-[34em] space-y-4">
            <Point
              icon={<MessagesSquare size={15} />}
              title={t('landing.p1.title')}
              body={t('landing.p1.body')}
            />
            <Point
              icon={<Hand size={15} />}
              title={t('landing.p2.title')}
              body={t('landing.p2.body')}
            />
            <Point
              icon={<Eye size={15} />}
              title={t('landing.p3.title')}
              body={t('landing.p3.body')}
            />
          </ul>
          <div className="mt-10 hidden max-w-[480px] sm:block">
            <BoardSketch />
          </div>
        </section>

        <div className="lg:pt-1">
          <SignInCard providers={config.data?.providers} />
        </div>
      </main>

      <footer className="pb-8 text-center text-xs text-muted">
        <Link to="/terms" className="hover:text-ink">
          {t('legal.terms')}
        </Link>
        <span className="mx-2">·</span>
        <Link to="/privacy" className="hover:text-ink">
          {t('legal.privacy')}
        </Link>
      </footer>
    </div>
  )
}
